// Manual price overrides → the calc the whole quote reads from.
// See the MANUAL PRICE OVERRIDE block in VexosClientCalculator.jsx for the
// rules; kept in its own module so it can be unit-tested without the page.
import { revisionRoundCost } from "./vexosClientPricing";

// The VEXOS setup fee is platform money, outside vizGross, so overriding it
// leaves the bundle and the motion package exactly where they were. Only the
// other lines put the discounts at stake (and ask the rep keep-or-remove).
export function overridesPutDiscountsAtStake(overrides) {
  return Object.keys(overrides || {}).some((id) => id !== "setupFee");
}

export function applyPriceOverrides(base, overrides, { isCondoLike, keepDiscounts = true }) {
  const ov = overrides || {};
  if (!Object.keys(ov).length) return base;
  // A setup-fee-only override never takes the quote off the rate card, even
  // if a stale "remove" choice is still saved on the quote.
  keepDiscounts = keepDiscounts || !overridesPutDiscountsAtStake(ov);

  // Every overridable leaf resolves to its override when present, else the
  // calculated value. `monthlyRate` is deliberately excluded — it flows
  // through the existing monthlyRateOverride path (CRM deduction + floor).
  const pick = (id, fallback) => (ov[id] !== undefined ? ov[id] : (Number(fallback) || 0));

  const dayToNightCost = pick("dayToNight", base.dayToNightCost);
  const additionalKfCost = pick("additionalKf", base.additionalKfCost);
  const animatedKfCost = pick("animatedKf", base.animatedKfCost);
  const masterOrbitCost = pick("masterOrbit", base.masterOrbitCost);
  const vexosCoverLandingPageCost = pick("coverLandingPage", base.vexosCoverLandingPageCost);
  const exteriorAddonsSubtotal = dayToNightCost + additionalKfCost + animatedKfCost + masterOrbitCost + vexosCoverLandingPageCost;

  const exteriorRendersCost = pick("exteriorRenders", base.exteriorRendersCost);
  const conceptualRendersCost = pick("conceptualRenders", base.conceptualRendersCost);
  const exteriorVRCost = pick("exteriorVR", base.exteriorVRCost);
  const droneCaptureFee = pick("droneCapture", base.droneCaptureFee);
  const droneSubtotal = droneCaptureFee;

  // Condo/SFH read `interior*`; community reads `home*`. Write both so the
  // mode-branching consumers downstream all see the override.
  const interiorRendersCost = pick("interiorRenders", isCondoLike ? base.interiorRendersCost : base.homeRendersCost);
  const interiorVRCost = pick("interiorVR", isCondoLike ? base.interiorVRCost : base.homeVRCost);

  const amenityRendersCost = pick("amenityRenders", base.amenityRendersCost);
  const amenityVRCost = pick("amenityVR", base.amenityVRCost);
  const amenitySubtotal = amenityRendersCost + amenityVRCost;
  // The Real-Time Configurator has no override of its own (the rep prices it
  // through its categories), but it is visualization scope, so it must stay
  // in the rebuilt gross below or an overridden quote would drop it.
  const vrConfiguratorCost = Number(base.vrConfiguratorCost) || 0;

  const aiMotionCost = pick("aiMotion", base.aiMotionCost);
  const cinematicCost = pick("cinematic", base.cinematicCost);
  const videoAnimationGross = aiMotionCost + cinematicCost;
  // Kept: the motion package % the rate card earned re-applies to the
  // overridden video lines (still needs both products priced). Removed: it's
  // an incentive, so both video lines bill at face value.
  const motionPackageActive = keepDiscounts && !!base.motionPackageActive && aiMotionCost > 0 && cinematicCost > 0;
  const motionPackagePct = motionPackageActive ? (Number(base.motionPackagePct) || 0) : 0;
  const motionPackageSavings = Math.round(videoAnimationGross * (motionPackagePct / 100));
  const videoAnimationCost = videoAnimationGross - motionPackageSavings;

  // Floor-plan volume pricing survives as a rate — the line costs what it
  // always cost — but it reports that amount as its own gross so no volume
  // savings row is ever drawn.
  const floorPlans2DCost = pick("floorPlans2D", base.floorPlans2DCost);
  const floorPlans3DCost = pick("floorPlans3D", base.floorPlans3DCost);
  const floorPlansCost = floorPlans2DCost + floorPlans3DCost;
  // With discounts kept, an untouched floor-plan line keeps its volume-savings
  // row; an overridden one (or any line once discounts are removed) reports
  // its amount as its own gross.
  const planVolume = (id, prefix, cost) => (keepDiscounts && ov[id] === undefined)
    ? {
        [`${prefix}Gross`]: base[`${prefix}Gross`],
        [`${prefix}Pct`]: base[`${prefix}Pct`],
        [`${prefix}Savings`]: base[`${prefix}Savings`],
      }
    : { [`${prefix}Gross`]: cost, [`${prefix}Pct`]: 0, [`${prefix}Savings`]: 0 };

  const vizGross =
    exteriorAddonsSubtotal + exteriorRendersCost + conceptualRendersCost + exteriorVRCost +
    interiorRendersCost + interiorVRCost + amenitySubtotal + vrConfiguratorCost + droneSubtotal +
    videoAnimationCost + floorPlansCost;
  // Kept: the bundle tier comes from the module count, which an override
  // never changes, and its % applies to the overridden gross. Removed: the
  // visualization scope nets to exactly the sum of its lines.
  const bundlePct = keepDiscounts ? (Number(base.bundle?.pct) || 0) : 0;
  const bundleSavings = Math.round(vizGross * (bundlePct / 100));
  const vizNet = vizGross - bundleSavings;

  // Revision rounds are priced as a % of net viz scope, so the unit cost
  // moves with an overridden line — unless the rep overrode the revisions
  // line itself, which then wins outright.
  const revisionRounds = Number(base.revisionRounds) || 0;
  const revisionUnitCost = revisionRoundCost(vizNet);
  const revisionsCost = ov.revisions !== undefined ? ov.revisions : revisionUnitCost * revisionRounds;
  const travelCost = pick("travel", base.travelCost);

  // Monthly (and therefore the recurring contract value) rides the separate
  // monthlyRateOverride path, so it carries through untouched.
  const setupFee = pick("setupFee", base.setupFee);
  const oneTimeAllIn = setupFee + vizNet + revisionsCost + travelCost;

  return {
    ...base,
    setupFee,
    dayToNightCost,
    additionalKfCost,
    animatedKfCost,
    masterOrbitCost,
    vexosCoverLandingPageCost,
    exteriorAddonsSubtotal,
    exteriorRendersCost,
    conceptualRendersCost,
    exteriorVRCost,
    droneCaptureFee,
    droneSubtotal,
    interiorRendersCost,
    interiorVRCost,
    homeRendersCost: interiorRendersCost,
    homeVRCost: interiorVRCost,
    amenityRendersCost,
    amenityVRCost,
    amenitySubtotal,
    aiMotionCost,
    cinematicCost,
    videoAnimationGross,
    videoAnimationCost,
    floorPlans2DCost,
    ...planVolume("floorPlans2D", "floorPlans2D", floorPlans2DCost),
    floorPlans3DCost,
    ...planVolume("floorPlans3D", "floorPlans3D", floorPlans3DCost),
    floorPlansCost,
    vizGross,
    vizNet,
    // Removed: every discount zeroed and named as such, so the surfaces that
    // gate on `savings > 0` / `pct > 0` drop their rows without needing to
    // know about overrides at all. Kept: the discounts read as normal.
    manualPricing: !keepDiscounts,
    bundle: keepDiscounts ? base.bundle : { ...(base.bundle || {}), tier: 0, label: null, pct: 0 },
    bundleSavings,
    motionPackageActive,
    motionPackagePct,
    motionPackageSavings,
    revisionUnitCost,
    revisionsCost,
    travelCost,
    oneTimeAllIn,
    grandTotal: oneTimeAllIn + (Number(base.recurringContractValue) || 0),
  };
}
